import React, { useEffect, useRef } from 'react';
import { IoClose, IoMusicalNotes } from 'react-icons/io5';
import { useLanguage } from '../i18n/LanguageContext';
import { useLyricsPosition } from '../hooks/useLyricsPosition';

// Fullscreen karaoke mode - a fixed, viewport-covering overlay independent
// of VoiceRoomPanel's own video-grid overlay chain (VideoWatchOverlay/
// ScreenShareOverlay/VideoGridOverlay), since lyrics have nothing to do with
// whatever's showing there and should stay available (or be opened) even
// while a watch-party/screen share is also active. Below .KnockModal's
// z-index 40 - an incoming knock still deserves to interrupt karaoke mode.
// offsetSec comes from VoiceRoomPanel's single shared useLyricsSyncOffset()
// call (see useLyricsPosition.js) - same value LyricsPanel's slider adjusts,
// so a change made there is reflected here immediately, including while
// this overlay is already open, not just the next time it's opened fresh.
export default function LyricsOverlay({ musicState, onClose, offsetSec }) {
  const { t } = useLanguage();
  const { lines, lyricsStatus, plainText, activeIndex } = useLyricsPosition(musicState, offsetSec);
  const linesRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (activeIndex < 0 || !linesRef.current) return;
    const el = linesRef.current.querySelector(`[data-line-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeIndex]);

  const nowPlaying = musicState && musicState.nowPlaying;

  return (
    <div className="LyricsOverlay">
      <div className="LyricsOverlayHeader">
        <span className="LyricsOverlayTitle">
          <IoMusicalNotes />
          {nowPlaying ? nowPlaying.title : t('lyrics.panelTitle')}
        </span>
        <button className="LyricsOverlayClose" onClick={onClose} title={t('common.close')}><IoClose /></button>
      </div>

      <div className="LyricsOverlayBody">
        {lyricsStatus === 'pending' && <div className="descGray">{t('lyrics.loading')}</div>}
        {lyricsStatus === 'notFound' && <div className="descGray">{t('lyrics.notFound')}</div>}
        {lyricsStatus === 'failed' && <div className="descGray">{t('lyrics.failed')}</div>}

        {lyricsStatus === 'plainOnly' && (
          <div className="LyricsOverlayPlainText">{plainText}</div>
        )}

        {lyricsStatus === 'ready' && lines && (
          <div className="LyricsOverlayLines" ref={linesRef}>
            {lines.map((line, i) => (
              <div
                key={i}
                data-line-index={i}
                className={`LyricsOverlayLine ${i === activeIndex ? 'LyricsOverlayLineActive' : ''}`}
              >
                {line.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
